import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { CheckCircle, XCircle, Star, Filter } from "lucide-react";

export default function AdminRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [filter, setFilter] = useState("all"); // all, rated, unrated, approved, rejected, pending
  const [ratings, setRatings] = useState({}); // Store ratings for each request

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    const snap = await getDocs(collection(db, "myspacerequests"));
    const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setRequests(data);
    setLoading(false);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredRequests.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredRequests.map((r) => r.id));
    }
  };

  const handleRatingChange = (id, value) => {
    const numValue = Number(value);
    if (numValue >= 1 && numValue <= 10) {
      setRatings({ ...ratings, [id]: numValue });
    } else if (value === "") {
      setRatings({ ...ratings, [id]: "" });
    }
  };

  const approveSelected = async () => {
    if (selectedIds.length === 0) return alert("No requests selected");

    const confirmPublish = window.confirm(
      `Approve and publish ${selectedIds.length} selected request(s)?`
    );
    if (!confirmPublish) return;

    setLoading(true);

    for (const id of selectedIds) {
      const req = requests.find((r) => r.id === id);
      const rating = ratings[id] || null;

      // Move to myspace collection
      await setDoc(doc(db, "myspace", req.id), {
        title: req.title,
        author: req.author,
        email: req.email,
        full: req.full,
        short: req.short,
        type: req.type,
        college: req.college,
        rating: rating,
        editable: true,
        publishedAt: serverTimestamp(),
        createdAt: req.createdAt,
      });

      // Update request status
      await updateDoc(doc(db, "myspacerequests", req.id), {
        status: "approved",
        published: true,
        approvedAt: serverTimestamp(),
        rating: rating,
      });
    }

    setSelectedIds([]);
    setRatings({});
    await fetchRequests();
    setLoading(false);
  };

  const rejectSelected = async () => {
    if (selectedIds.length === 0) return alert("No requests selected");

    const reason = prompt("Reason for rejection:");
    if (!reason) return;

    setLoading(true);

    for (const id of selectedIds) {
      await updateDoc(doc(db, "myspacerequests", id), {
        status: "rejected",
        rejectedAt: serverTimestamp(),
        rejectionReason: reason,
      });
    }

    setSelectedIds([]);
    await fetchRequests();
    setLoading(false);
  };

  const approveSingle = async (req) => {
    const rating = ratings[req.id] || null;

    const confirmPublish = window.confirm("Publish this writing?");
    if (!confirmPublish) return;

    setLoading(true);

    // Move to myspace
    await setDoc(doc(db, "myspace", req.id), {
      title: req.title,
      author: req.author,
      email: req.email,
      full: req.full,
      short: req.short,
      type: req.type,
      college: req.college,
      rating: rating,
      editable: true,
      publishedAt: serverTimestamp(),
      createdAt: req.createdAt,
    });

    // Update request status
    await updateDoc(doc(db, "myspacerequests", req.id), {
      status: "approved",
      published: true,
      approvedAt: serverTimestamp(),
      rating: rating,
    });

    setRatings({ ...ratings, [req.id]: undefined });
    await fetchRequests();
    setLoading(false);
  };

  const rejectSingle = async (req) => {
    const reason = prompt("Reason for rejection:");
    if (!reason) return;

    setLoading(true);

    await updateDoc(doc(db, "myspacerequests", req.id), {
      status: "rejected",
      rejectedAt: serverTimestamp(),
      rejectionReason: reason,
    });

    await fetchRequests();
    setLoading(false);
  };

  // Filter logic
  const filteredRequests = requests.filter((req) => {
    if (filter === "all") return true;
    if (filter === "rated") return req.rating != null;
    if (filter === "unrated") return req.rating == null;
    if (filter === "approved") return req.status === "approved";
    if (filter === "rejected") return req.status === "rejected";
    if (filter === "pending") return !req.status || req.status === "pending";
    return true;
  });

  const stats = {
    total: requests.length,
    rated: requests.filter((r) => r.rating != null).length,
    unrated: requests.filter((r) => r.rating == null).length,
    approved: requests.filter((r) => r.status === "approved").length,
    rejected: requests.filter((r) => r.status === "rejected").length,
    pending: requests.filter((r) => !r.status || r.status === "pending").length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Super Admin Panel</h1>
        <p className="text-gray-600">Manage MySpace submission requests</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
          <p className="text-sm text-blue-600">Total</p>
        </div>
        <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-green-700">{stats.rated}</p>
          <p className="text-sm text-green-600">Rated</p>
        </div>
        <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-orange-700">{stats.unrated}</p>
          <p className="text-sm text-orange-600">Unrated</p>
        </div>
        <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-purple-700">{stats.approved}</p>
          <p className="text-sm text-purple-600">Approved</p>
        </div>
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-red-700">{stats.rejected}</p>
          <p className="text-sm text-red-600">Rejected</p>
        </div>
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
          <p className="text-sm text-yellow-600">Pending</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-2 rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={20} />
          <h3 className="font-bold text-lg">Filters</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {["all", "rated", "unrated", "approved", "rejected", "pending"].map(
            (f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg font-medium transition ${
                  filter === f
                    ? "bg-black text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            )
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="bg-black text-white rounded-xl p-4 mb-6 flex items-center justify-between">
          <p className="font-bold">
            {selectedIds.length} request(s) selected
          </p>
          <div className="flex gap-3">
            <button
              onClick={approveSelected}
              className="px-5 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-medium transition flex items-center gap-2"
            >
              <CheckCircle size={18} />
              Approve Selected
            </button>
            <button
              onClick={rejectSelected}
              className="px-5 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-medium transition flex items-center gap-2"
            >
              <XCircle size={18} />
              Reject Selected
            </button>
            <button
              onClick={() => setSelectedIds([])}
              className="px-5 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg font-medium transition"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Select All */}
      {filteredRequests.length > 0 && (
        <div className="mb-4">
          <label className="flex items-center gap-2 font-medium cursor-pointer w-fit">
            <input
              type="checkbox"
              checked={
                selectedIds.length === filteredRequests.length &&
                filteredRequests.length > 0
              }
              onChange={toggleSelectAll}
              className="w-5 h-5 cursor-pointer"
            />
            Select All ({filteredRequests.length})
          </label>
        </div>
      )}

      {/* Requests List */}
      {filteredRequests.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <p className="text-gray-500 text-lg">
            No requests found for this filter
          </p>
        </div>
      )}

      <div className="space-y-4">
        {filteredRequests.map((req) => (
          <div
            key={req.id}
            className={`border-2 rounded-xl p-5 bg-white shadow-sm transition ${
              selectedIds.includes(req.id)
                ? "border-black bg-gray-50"
                : "border-gray-200"
            }`}
          >
            <div className="flex items-start gap-4">
              {/* Checkbox */}
              <input
                type="checkbox"
                checked={selectedIds.includes(req.id)}
                onChange={() => toggleSelect(req.id)}
                className="w-5 h-5 mt-1 cursor-pointer"
              />

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h2 className="text-xl font-bold">{req.title}</h2>
                    <p className="text-sm text-gray-600">
                      By {req.author} · {req.college}
                    </p>
                    <div className="flex gap-2 mt-1">
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                        {req.type}
                      </span>
                      {req.status && (
                        <span
                          className={`px-2 py-1 text-xs rounded-full font-medium ${
                            req.status === "approved"
                              ? "bg-green-100 text-green-700"
                              : req.status === "rejected"
                              ? "bg-red-100 text-red-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {req.status}
                        </span>
                      )}
                      {req.rating != null && (
                        <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs rounded-full font-medium flex items-center gap-1">
                          <Star size={12} fill="currentColor" />
                          {req.rating}/10
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Rating Input */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">
                      Rate:
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={ratings[req.id] ?? req.rating ?? ""}
                      onChange={(e) =>
                        handleRatingChange(req.id, e.target.value)
                      }
                      placeholder="1-10"
                      className="w-16 px-2 py-1 border-2 border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-black focus:border-transparent"
                    />
                  </div>
                </div>

                <p className="mt-3 text-gray-800 line-clamp-2">
                  {req.full}
                </p>

                {req.email && (
                  <p className="text-xs text-gray-500 mt-2">
                    Email: {req.email}
                  </p>
                )}

                {req.rejectionReason && (
                  <p className="text-sm text-red-600 mt-2 bg-red-50 p-2 rounded">
                    Rejection Reason: {req.rejectionReason}
                  </p>
                )}

                {/* Actions */}
                {req.status !== "approved" && req.status !== "rejected" && (
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => approveSingle(req)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition flex items-center gap-2"
                    >
                      <CheckCircle size={16} />
                      Approve
                    </button>

                    <button
                      onClick={() => rejectSingle(req)}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition flex items-center gap-2"
                    >
                      <XCircle size={16} />
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}